import { businessIdsHtml, escapeHtml as esc, printHtml, type DocumentSalon } from '../../utils/invoice'
import { brandColors } from '../../data/brandColors'
import { exVat } from '../../core/rules/revenue'
import { money } from '../../core/format/currency'
import { fullDate, fromIso } from '../../core/format/dates'
import { clock, refundMethodLabel } from './kassaLabels'
import type { KuurStop } from '../../core/types/kassa'

type Salon = DocumentSalon

/**
 * De creditnota van een gestopte kuur (docs/KASSA-ABONNEMENTEN.md §4.4): een eigen nummer
 * ("C2026-0001"), de kuur zoals gekocht, en de terugbetaling met negatieve btw. Met de echte server
 * wordt dit een PDF, net als de kassabon.
 */
export function kuurCreditNoteHtml(k: KuurStop, salon: Salon): string {
  const vatCents = k.refundCents - exVat(k.refundCents, k.vatRate)
  const neg = (cents: number) => (cents === 0 ? money(0) : `− ${money(cents)}`)
  const back =
    k.refundCents === 0
      ? 'Er gaat niets terug.'
      : k.method === 'tegoed'
        ? `Bijgeschreven als tegoed bij ${salon.name}.`
        : `Terugbetaald: ${refundMethodLabel[k.method].toLowerCase()}.`

  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>Creditnota ${esc(k.number)}</title>
<style>
@page{margin:12mm}
body{font-family:Inter,system-ui,sans-serif;color:${brandColors.ink};margin:40px auto;max-width:460px;font-size:14px;line-height:1.5}
@media print{body{margin:0 auto}}
h1{font-family:Fraunces,Georgia,serif;font-weight:300;font-size:26px;margin:24px 0 4px}
.muted{color:${brandColors.muted}}.small{font-size:12px}
table{width:100%;border-collapse:collapse;margin-top:20px}td{text-align:left;padding:7px 0;border-bottom:1px solid ${brandColors.line};vertical-align:top}
td.r{text-align:right;white-space:nowrap;padding-left:12px}.tot td{border:0;padding:3px 0}.big td{font-size:16px;padding-top:10px}
</style></head><body>
<strong>${esc(salon.name)}</strong><div class="muted small">${esc(salon.address)} · ${esc(salon.email)}<br>${businessIdsHtml(salon, ' · ')}</div>
<h1>Creditnota</h1>
<div class="muted">${esc(k.number)} · ${esc(fullDate(fromIso(k.day)))} ${esc(clock(k.at))} · ${esc(k.customerName)}</div>
<table><tbody>
<tr><td>Kuur ${esc(k.treatmentName)} · ${k.sessionsTotal}×<div class="muted small">Vooruit betaald ${esc(money(k.paidCents))} · ${k.sessionsDone} van ${k.sessionsTotal} sessies gedaan</div></td><td class="r"></td></tr>
<tr><td>Terugbetaling niet-gebruikte sessies<div class="muted small">${esc(k.explanation)}${k.reason ? `<br>${k.refundCents === k.suggestedCents ? 'Toelichting' : `Aangepast (voorstel ${esc(money(k.suggestedCents))})`}: ${esc(k.reason)}` : ''}</div></td><td class="r">${esc(neg(k.refundCents))}</td></tr>
</tbody></table>
<table><tbody class="tot">
<tr class="big"><td>Totaal</td><td class="r">${esc(neg(k.refundCents))}</td></tr>
${k.refundCents > 0 ? `<tr><td class="muted">Waarvan btw ${k.vatRate}%</td><td class="r">${esc(neg(vatCents))}</td></tr>` : ''}
</tbody></table>
<p style="margin-top:20px">${esc(back)}</p>
<p class="muted small" style="margin-top:32px">De kuur is hiermee beëindigd.</p>
</body></html>`
}

export const printKuurCreditNote = (k: KuurStop, salon: Salon) => printHtml(kuurCreditNoteHtml(k, salon), `creditnota-${k.number}.html`, 'Creditnota')
